/* redux/projectState/entry.js
 *
 * Pure, store-free helpers over a target-list entry — or a whole target list. A TargetEntry is
 * either an IndividualTarget ({ mapCase, ... }) or a GroupTarget
 * ({ groupId, mapCases, ... }); see slice.js for the typedefs. Keeping these
 * shape-agnostic helpers out of access.js lets callers (and unit tests) reason
 * about an entry without pulling in the redux store.
 *
 * Tests in test/redux/projectState.test.js.
 */
import { structureKey } from 'BMapsModel';

/** @param {import('./slice').TargetEntry} entry */
export function isGroupEntry(entry) {
    return entry?.groupId != null;
}

/**
 * A stable string id for any entry — the group's id, or the mount-free
 * structureKey of an individual target. A shape-agnostic key (React key / dedup /
 * matching) so callers need not branch on entry shape. Note: for individuals this
 * is the mount-free structureKey, which is NOT a durable cross-session id for user
 * uploads / folds — see model/structureRef.js.
 * @param {import('./slice').TargetEntry} entry
 * @returns {string?}
 */
export function getEntryId(entry) {
    return entry.groupId ?? structureKey(entry.mapCase);
}

/**
 * All MapCases for an entry: a one-element array for an individual target, the
 * member array for a group.
 * @param {import('./slice').TargetEntry} entry
 * @returns {import('BMapsModel').MapCase[]}
 */
export function getEntryMapCases(entry) {
    return entry.mapCases ?? [entry.mapCase];
}

/**
 * The entry iff it is an individual target — not a group, and not null/undefined. Use this at
 * every getTargetInfoMap().get() site: it collapses the "is it defined? is it a group? now read
 * .isTarget" guard into one call and structurally rules out destructuring undefined. Note that a
 * structure that is only a group MEMBER resolves (via getTargetInfoMap) to its GROUP entry, so
 * this correctly returns null for it — a panel member is not an individual target until pulled out.
 * @param {import('./slice').TargetEntry} [entry]
 * @returns {?import('./slice').IndividualTarget}
 */
export function asIndividualTarget(entry) {
    return entry && !isGroupEntry(entry) ? entry : null;
}

/**
 * Every member MapCase across a target list: a group contributes all of its members, an
 * individual contributes itself. (Equivalent to targetList.flatMap(getEntryMapCases).)
 * @param {import('./slice').TargetEntry[]} targetList
 * @returns {import('BMapsModel').MapCase[]}
 */
export function getTargetMapCases(targetList) {
    return targetList.flatMap(getEntryMapCases);
}
